/**
 * @name CadastroService
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Service para atualização de dados cadastrais (endereço e telefone)
 * @package services/supabase/entities
 */

import { supabase } from '../../../../supabaseConfig';

interface DadosEndereco {
     cep: string;
     logradouro: string;
     bairro: string;
     cidade: string;
     uf: string;
     numero?: string;
     complemento?: string;
}

interface DadosTelefone {
     ddd: string;
     numero: string;
     tipo: 'principal' | 'secundario';
}

export class CadastroService {
     /**
      * Atualiza ou cria endereço principal da pessoa
      */
     static async atualizarEnderecoPrincipal(
          pessoaId: number,
          organizacaoId: number,
          dados: DadosEndereco
     ) {
          try {
               // Primeiro, verificar se já existe endereço principal
               const { data: enderecoExistente } = await supabase
                    .from('enderecos')
                    .select('*')
                    .eq('fk_id_pessoa', pessoaId)
                    .eq('tipo', 'principal')
                    .eq('fk_id_organizacao', organizacaoId)
                    .single();

               const dadosEndereco = {
                    fk_id_pessoa: pessoaId,
                    fk_id_organizacao: organizacaoId,
                    tipo: 'principal' as const,
                    cep: dados.cep,
                    logradouro: dados.logradouro,
                    numero: dados.numero || '',
                    complemento: dados.complemento || null,
                    bairro: dados.bairro,
                    cidade: dados.cidade,
                    estado: dados.uf,
               };

               if (enderecoExistente) {
                    // Atualizar existente
                    const { data, error } = await supabase
                         .from('enderecos')
                         .update(dadosEndereco)
                         .eq('id', enderecoExistente.id)
                         .select()
                         .single();

                    return { data, error };
               } else {
                    // Criar novo
                    const { data, error } = await supabase
                         .from('enderecos')
                         .insert(dadosEndereco)
                         .select()
                         .single();

                    return { data, error };
               }
          } catch (error) {
               console.error('Erro ao atualizar endereço:', error);
               return { data: null, error };
          }
     }

     /**
      * Atualiza ou cria telefone principal da pessoa
      */
     static async atualizarTelefonePrincipal(
          pessoaId: number,
          organizacaoId: number,
          dados: DadosTelefone
     ) {
          try {
               // Primeiro, verificar se já existe telefone principal
               const { data: telefoneExistente } = await supabase
                    .from('telefones')
                    .select('*')
                    .eq('fk_id_pessoa', pessoaId)
                    .eq('tipo', 'principal')
                    .eq('fk_id_organizacao', organizacaoId)
                    .single();

               const dadosTelefone = {
                    fk_id_pessoa: pessoaId,
                    fk_id_organizacao: organizacaoId,
                    tipo: 'principal' as const,
                    ddd: dados.ddd,
                    numero: dados.numero,
               };

               if (telefoneExistente) {
                    // Atualizar existente
                    const { data, error } = await supabase
                         .from('telefones')
                         .update(dadosTelefone)
                         .eq('id', telefoneExistente.id)
                         .select()
                         .single();

                    return { data, error };
               } else {
                    // Criar novo
                    const { data, error } = await supabase
                         .from('telefones')
                         .insert(dadosTelefone)
                         .select()
                         .single();

                    return { data, error };
               }
          } catch (error) {
               console.error('Erro ao atualizar telefone:', error);
               return { data: null, error };
          }
     }

     /**
      * Busca endereço principal da pessoa
      */
     static async buscarEnderecoPrincipal(pessoaId: number, organizacaoId: number) {
          try {
               const { data, error } = await supabase
                    .from('enderecos')
                    .select('*')
                    .eq('fk_id_pessoa', pessoaId)
                    .eq('tipo', 'principal')
                    .eq('fk_id_organizacao', organizacaoId)
                    .maybeSingle(); // Use maybeSingle() em vez de single()

               if (error) {
                    console.warn('Erro ao buscar endereço:', error);
                    return { data: null, error: null }; // Não considerar erro se não encontrar
               }

               return { data, error: null };
          } catch (error) {
               console.error('Erro ao buscar endereço:', error);
               return { data: null, error: null }; // Não bloquear por erro de busca
          }
     }

     /**
      * Busca telefone principal da pessoa
      */
     static async buscarTelefonePrincipal(pessoaId: number, organizacaoId: number) {
          try {
               const { data, error } = await supabase
                    .from('telefones')
                    .select('*')
                    .eq('fk_id_pessoa', pessoaId)
                    .eq('tipo', 'principal')
                    .eq('fk_id_organizacao', organizacaoId)
                    .maybeSingle(); // Use maybeSingle() em vez de single()

               if (error) {
                    console.warn('Erro ao buscar telefone:', error);
                    return { data: null, error: null }; // Não considerar erro se não encontrar
               }

               return { data, error: null };
          } catch (error) {
               console.error('Erro ao buscar telefone:', error);
               return { data: null, error: null }; // Não bloquear por erro de busca
          }
     }

     /**
      * Atualiza dados completos de cadastro (endereço + telefone)
      */
     static async atualizarDadosCompletos(
          pessoaId: number,
          organizacaoId: number,
          dados: { endereco: DadosEndereco; telefone: DadosTelefone }
     ) {
          try {
               // Atualizar endereço
               const resultadoEndereco = await this.atualizarEnderecoPrincipal(
                    pessoaId,
                    organizacaoId,
                    dados.endereco
               );

               if (resultadoEndereco.error) {
                    throw new Error(`Erro ao atualizar endereço: ${JSON.stringify(resultadoEndereco.error)}`);
               }

               // Atualizar telefone
               const resultadoTelefone = await this.atualizarTelefonePrincipal(
                    pessoaId,
                    organizacaoId,
                    dados.telefone
               );

               if (resultadoTelefone.error) {
                    throw new Error(`Erro ao atualizar telefone: ${JSON.stringify(resultadoTelefone.error)}`);
               }

               return {
                    data: {
                         endereco: resultadoEndereco.data,
                         telefone: resultadoTelefone.data,
                    },
                    error: null,
               };
          } catch (error) {
               console.error('Erro ao atualizar dados completos:', error);
               return {
                    data: null,
                    error: error instanceof Error ? error : new Error('Erro desconhecido'),
               };
          }
     }
}

export default CadastroService;
